import type { MealFrontend, MealPlanFrontend, MealIngredientFrontend } from '../../../types/mealPlan';
import type {
  PDFMealData,
  PDFMealPlanData,
  PDFIngredient,
  PDFInstructionStep,
  PDFMealSlot,
  PDFMealPlanDay,
  MealType,
} from '../types';
import { DEFAULT_MEAL_TYPES, MS_IN_DAY } from '../constants';

const MEAL_TYPE_SET = new Set<MealType>(DEFAULT_MEAL_TYPES);

const normalizeMealType = (type: MealFrontend['mealType']): MealType => {
  if (MEAL_TYPE_SET.has(type as MealType)) {
    return type as MealType;
  }

  if (type === 'dessert') {
    return 'snack';
  }

  return 'snack';
};

const getMealTypesPerDay = (plan: MealPlanFrontend): MealType[] => {
  const rawTypes = plan.creationParameters?.mealTypes;
  if (!rawTypes || rawTypes.length === 0) {
    return DEFAULT_MEAL_TYPES;
  }

  const normalized = rawTypes
    .map((type) => normalizeMealType(type))
    .filter((type, index, self) => self.indexOf(type) === index);

  return normalized.length > 0 ? normalized : DEFAULT_MEAL_TYPES;
};

export const mapMealToPDF = (meal: MealFrontend): PDFMealData => {
  const partialMeal = meal as Partial<MealFrontend>;
  const ingredientList = Array.isArray(partialMeal.ingredients) ? partialMeal.ingredients : [];
  const instructionList = Array.isArray(partialMeal.instructions)
    ? partialMeal.instructions
    : typeof partialMeal.instructions === 'string'
      ? [partialMeal.instructions]
      : [];
  const safeInstructions = instructionList.length > 0 ? instructionList : ['No instructions provided.'];
  const nutrition = partialMeal.nutrition;

  return {
    id: meal.id,
    name: meal.mealName,
    description: undefined,
    tags: meal.tags,
    mealType: normalizeMealType(meal.mealType),
    prepTimeMinutes: meal.prepTimeMinutes ?? 0,
    cookTimeMinutes: meal.cookTimeMinutes ?? 0,
    servings: meal.servings ?? 0,
    costEstimate: meal.estimatedCostAud ?? 0,
    difficulty: meal.difficulty ?? '',
    cuisine: meal.cuisine ?? '',
    ingredients: ingredientList
      .filter((ing): ing is MealIngredientFrontend => Boolean(ing))
      .map(
        (ing: MealIngredientFrontend): PDFIngredient => ({
          name: ing.name ?? '',
          amount: typeof ing.amount === 'number' ? ing.amount : 0,
          unit: ing.unit ?? '',
          cost: typeof ing.costAud === 'number' ? ing.costAud : 0,
        }),
      ),
    instructions: safeInstructions.map(
      (text: string, index: number): PDFInstructionStep => ({
        stepNumber: index + 1,
        text,
      }),
    ),
    macros: {
      calories: nutrition?.caloriesPerServing ?? 0,
      protein: nutrition?.proteinG ?? 0,
      carbs: nutrition?.carbsG ?? 0,
      fat: nutrition?.fatG ?? 0,
      fiber: nutrition?.fiberG ?? 0,
      sugar: nutrition?.sugarG ?? 0,
      sodium: nutrition?.sodiumMg ?? 0,
    },
  };
};

export const mapMealPlanToPDF = (plan: MealPlanFrontend): PDFMealPlanData => {
  const planName = plan.label || plan.creationParameters?.label || 'Meal Plan';
  const patientName = plan.assignmentInfo?.patientName || '';
  const dietitianName =
    plan.assignmentInfo?.assignedByName || plan.creatorName || 'BiteWise Dietitian';
  const generatedAt = plan.mealPlanData.generatedAt || plan.createdAt;
  const mealTypesPerDay = getMealTypesPerDay(plan);

  const mealSequence: PDFMealSlot[] = plan.mealPlanData.meals.map((meal) => ({
    type: normalizeMealType(meal.mealType),
    meal: mapMealToPDF(meal),
  }));

  const createdDate = new Date(generatedAt || plan.createdAt);
  const startDate = Number.isNaN(createdDate.getTime()) ? new Date() : createdDate;
  const totalMeals = mealSequence.length;
  const fallbackDays =
    plan.mealPlanData.planDurationDays ||
    plan.planDurationDays ||
    Math.max(1, Math.ceil(totalMeals / Math.max(1, mealTypesPerDay.length)));
  const mealsPerDay = Math.max(1, mealTypesPerDay.length);
  const derivedDurationDays = Math.max(1, Math.ceil(totalMeals / mealsPerDay));
  const durationDays = Math.max(fallbackDays, derivedDurationDays);

  const days: PDFMealPlanDay[] = Array.from({ length: durationDays }, (_, dayIndex) => {
    const sliceStart = dayIndex * mealsPerDay;
    const sliceEnd = sliceStart + mealsPerDay;
    const daySlots: PDFMealSlot[] = mealSequence.slice(sliceStart, sliceEnd);

    const dayDate = new Date(startDate.getTime() + dayIndex * MS_IN_DAY);

    return {
      date: dayDate.toISOString(),
      dayLabel: `Day ${dayIndex + 1}`,
      meals: daySlots,
    };
  });

  return {
    id: plan.id,
    name: planName,
    patientName,
    dietitianName,
    generatedAt,
    days,
    mealTypesPerDay,
    mealSequence,
  };
};
